import { useState } from 'preact/hooks';
import { FormGroup, InputLabel } from '@components/async-form';
import { ModalForm } from '@components/modal-form';
import { BtnSecondary } from '@components/buttons';
import { rpx } from 'client/lib/rpx-client';
import type { State } from './state';

type Props = {
  series: State['series'];
  onChanged(): void;
};

export function SeriesManagement({ series, onChanged }: Props) {
  const [open, setOpen] = useState<undefined | 'edit' | 'extend' | 'end'>(undefined);
  if (series.endedEarlyAt) {
    return undefined;
  }
  return (
    <>
      <div class="flex gap-2">
        <BtnSecondary onClick={() => setOpen('edit')}>Edit details</BtnSecondary>
        <BtnSecondary onClick={() => setOpen('extend')}>Extend</BtnSecondary>
        <BtnSecondary onClick={() => setOpen('end')}>End early</BtnSecondary>
      </div>
      {open === 'edit' && (
        <EditModal
          series={series}
          onClose={() => setOpen(undefined)}
          onDone={() => {
            setOpen(undefined);
            onChanged();
          }}
        />
      )}
      {open === 'extend' && (
        <ExtendModal
          series={series}
          onClose={() => setOpen(undefined)}
          onDone={() => {
            setOpen(undefined);
            onChanged();
          }}
        />
      )}
      {open === 'end' && (
        <EndEarlyModal
          series={series}
          onClose={() => setOpen(undefined)}
          onDone={() => {
            setOpen(undefined);
            onChanged();
          }}
        />
      )}
    </>
  );
}

type ModalProps = {
  series: State['series'];
  onClose(): void;
  onDone(): void;
};

function EditModal({ series, onClose, onDone }: ModalProps) {
  const [title, setTitle] = useState(series.title);
  const [calendarTitle, setCalendarTitle] = useState(series.calendarTitle);
  const [description, setDescription] = useState(series.calendarDescription ?? '');
  const [bridgeLink, setBridgeLink] = useState(series.bridgeLink ?? '');
  const [sendInvites, setSendInvites] = useState(series.sendCalendarInvites);
  return (
    <ModalForm
      onClose={onClose}
      title="Edit series details"
      confirmButtonText="Save"
      onSubmit={async () => {
        await rpx.meetingSeries.updateMeetingSeries({
          courseId: series.courseId,
          seriesId: series.id,
          title,
          calendarTitle,
          calendarDescription: description,
          bridgeLink: bridgeLink.trim(),
          sendCalendarInvites: sendInvites,
        });
        onDone();
      }}
    >
      <p class="mb-3 text-sm text-gray-600">
        Schedule (cadence, day, time, dates) can't be changed. Use Extend or End early for those.
        Edits to bridge link and calendar title re-send the recurring invite to all attendees with
        an updated entry.
      </p>
      <FormGroup prop="title" class="mb-3">
        <InputLabel>Series title (internal)</InputLabel>
        <input
          type="text"
          class="inline-ruz-input w-full"
          value={title}
          onChange={(e: any) => setTitle(e.target.value)}
        />
      </FormGroup>
      <FormGroup prop="calendarTitle" class="mb-3">
        <InputLabel>Calendar title (visible to students)</InputLabel>
        <input
          type="text"
          class="inline-ruz-input w-full"
          value={calendarTitle}
          onChange={(e: any) => setCalendarTitle(e.target.value)}
        />
      </FormGroup>
      <FormGroup prop="bridgeLink" class="mb-3">
        <InputLabel>Bridge link</InputLabel>
        <input
          type="url"
          class="inline-ruz-input w-full"
          value={bridgeLink}
          onChange={(e: any) => setBridgeLink(e.target.value)}
          placeholder="https://zoom.us/j/..."
        />
      </FormGroup>
      <FormGroup prop="calendarDescription" class="mb-3">
        <InputLabel>Calendar description</InputLabel>
        <textarea
          class="inline-ruz-input w-full"
          rows={4}
          value={description}
          onChange={(e: any) => setDescription(e.target.value)}
        />
      </FormGroup>
      <label class="flex gap-2 items-center text-sm">
        <input
          type="checkbox"
          checked={sendInvites}
          onChange={(e: any) => setSendInvites(e.target.checked)}
        />
        Send calendar invites for this series
      </label>
    </ModalForm>
  );
}

function ExtendModal({ series, onClose, onDone }: ModalProps) {
  const initial = toYmd(series.endDate);
  const [newEndDate, setNewEndDate] = useState(initial);
  const disabled = newEndDate <= initial;
  return (
    <ModalForm
      onClose={onClose}
      title="Extend series"
      confirmButtonText="Extend"
      onSubmit={async () => {
        await rpx.meetingSeries.extendSeries({
          courseId: series.courseId,
          seriesId: series.id,
          newEndDate,
        });
        onDone();
      }}
    >
      <p class="mb-3 text-sm text-gray-600">
        Adds new sessions through the new end date at the same cadence. The recurring invite is
        re-sent with the new run length.
      </p>
      <FormGroup prop="newEndDate">
        <InputLabel>New end date</InputLabel>
        <input
          type="date"
          class="inline-ruz-input"
          value={newEndDate}
          min={initial}
          onChange={(e: any) => setNewEndDate(e.target.value)}
        />
      </FormGroup>
      {disabled && (
        <p class="mt-2 text-xs text-amber-700">New end date must be after the current end date.</p>
      )}
    </ModalForm>
  );
}

function EndEarlyModal({ series, onClose, onDone }: ModalProps) {
  const [reason, setReason] = useState('');
  return (
    <ModalForm
      onClose={onClose}
      title="End series early?"
      confirmButtonText="End series"
      onSubmit={async () => {
        await rpx.meetingSeries.endSeriesEarly({
          courseId: series.courseId,
          seriesId: series.id,
          reason: reason.trim() || undefined,
        });
        onDone();
      }}
    >
      <p class="mb-3 text-sm text-gray-600">
        Cancels every future session of this series. Students receive a cancellation for each
        remaining occurrence; past sessions are unaffected.
      </p>
      <FormGroup prop="reason">
        <InputLabel>Reason (optional, included in cancellation)</InputLabel>
        <input
          type="text"
          class="inline-ruz-input w-full"
          value={reason}
          onChange={(e: any) => setReason(e.target.value)}
          placeholder="Cohort wrapped up"
        />
      </FormGroup>
    </ModalForm>
  );
}

function toYmd(d: Date | string): string {
  if (typeof d === 'string') {
    return d.slice(0, 10);
  }
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
